/**
 * Single source of truth: menu items + route roles (TZ navigation).
 * Keep Sidebar and AppRoutes in sync via this config.
 *
 * ERP «Руководитель» / «Руководитель отдела» (leader, dept_leader) see only
 * hiring requests, employees, candidate portrait, department profiles, and
 * their personal profile — not the rest of the HR workspace.
 *
 * Personal menu (правка №5, [Claude] 2026-11-02): every user may reorder items,
 * hide items and pick a start page. Those settings only change what is shown —
 * access is still decided by `roles` below. New items added here appear for
 * everyone at the end of a personalised menu, visible.
 */
import { pathAllowedByRights, rightsOn } from "./accessRights";

/** @typedef {{ to: string, label: string, roles: string[], icon?: string, hidden?: boolean }} NavLeaf */
/** @typedef {{ key: string, label: string, icon?: string, roles: string[], children: NavLeaf[], hidden?: boolean }} NavGroup */
/** @typedef {NavLeaf | NavGroup} NavItem */
/** @typedef {{ order: string[], hidden: string[], children: Object<string, string[]>, home: (string|null) }} MenuPrefs */

/** ERP roles that use the HR panel like HR staff (not department heads). */
const ERP_STAFF_ROLES = ["superadmin", "admin", "manager", "senior_manager"];

/** ERP display names «Руководитель» and «Руководитель отдела». */
export const LEADER_ROLES = ["leader", "dept_leader"];

const uniq = (...lists) => Array.from(new Set(lists.flat()));

const withErpStaff = (roles) => uniq(roles, ERP_STAFF_ROLES);

const withLeaders = (roles) => uniq(roles, LEADER_ROLES);

/** Any signed-in panel role may open the personal profile (hidden from the main menu). */
const withAllPanelRoles = (roles) => uniq(roles, ERP_STAFF_ROLES, LEADER_ROLES);

export function isLeaderRole(role) {
    return LEADER_ROLES.includes(String(role || "").trim());
}

/** @type {NavItem[]} */
export const NAV_ITEMS = [
    { to: "/dashboard", label: "Главная", icon: "DashboardIcon", roles: withErpStaff(["owner", "lead", "hr", "dev", "art"]) },
    { to: "/ai-assistant", label: "AI-консультант", icon: "RobotIcon", roles: withErpStaff(["owner", "lead", "hr", "dev", "art"]) },
    { to: "/requests", label: "Заявки", icon: "InboxIcon", roles: withLeaders(withErpStaff(["lead", "hr", "dev", "owner"])) },
    { to: "/vacancies", label: "Вакансии", icon: "BriefcaseIcon", roles: withErpStaff(["hr", "owner", "dev", "lead"]) },
    { to: "/candidates", label: "Кандидаты", icon: "UsersIcon", roles: withErpStaff(["hr", "dev", "owner"]) },
    {
        to: "/candidates/sync-issues",
        label: "Синхронизация кандидатов с HH",
        icon: "UsersIcon",
        roles: withErpStaff(["hr", "dev", "owner"]),
        hidden: true,
    },
    {
        key: "group:documents",
        label: "Работа с документами",
        icon: "FileTextIcon",
        roles: withErpStaff(["hr", "dev", "owner"]),
        children: [
            { to: "/candidate-documents", label: "Документы кандидатов", roles: withErpStaff(["hr", "dev", "owner"]) },
            { to: "/employee-contracts", label: "Договоры кандидатов", roles: withErpStaff(["hr", "dev", "owner"]) },
        ],
    },
    { to: "/learning", label: "Обучение", icon: "ClipboardIcon", roles: withErpStaff(["hr", "dev", "owner"]) },
    { to: "/employees", label: "Сотрудники", icon: "CheckCircleIcon", roles: withLeaders(withErpStaff(["hr", "owner", "dev"])) },
    { to: "/employees/excel", label: "Excel сотрудников", icon: "FileTextIcon", roles: withErpStaff(["hr", "owner", "dev"]), hidden: true },
    // Правка №11: «Доступы» — открывается кнопкой во вкладке «Сотрудники»; кто может менять права, решает backend.
    { to: "/employees/access", label: "Доступы", icon: "UsersIcon", roles: withErpStaff(["hr", "owner", "dev"]), hidden: true },
    { to: "/contacts", label: "Контакты HH", icon: "UsersIcon", roles: withErpStaff(["hr", "owner", "dev"]) },
    {
        key: "group:tests",
        label: "Тесты",
        icon: "ClipboardIcon",
        roles: withErpStaff(["hr", "owner", "dev"]),
        children: [
            { to: "/tests", label: "Создание тестов", roles: withErpStaff(["hr", "owner", "dev"]) },
            { to: "/tests/results", label: "Результаты тестов", roles: withErpStaff(["hr", "owner", "dev"]) },
            { to: "/tests/unlinked", label: "Непривязанные результаты", roles: withErpStaff(["hr", "owner", "dev"]) },
            { to: "/tests/adaptation", label: "Адаптация", roles: withLeaders(withErpStaff(["hr", "owner", "dev"])) },
        ],
    },
    { to: "/calendar", label: "Календарь", icon: "CalendarIcon", roles: withErpStaff(["hr", "owner", "dev"]) },
    { to: "/autopilot", label: "Автопилот найма", icon: "CalendarIcon", roles: withErpStaff(["hr", "owner", "dev"]) },
    { to: "/analytics", label: "Статистика кандидатов", icon: "BarChartIcon", roles: withErpStaff(["owner", "dev", "hr"]) },
    { to: "/events", label: "События", icon: "CalendarIcon", roles: withErpStaff(["hr", "dev", "owner"]) },
    { to: "/calls", label: "Звонки", icon: "PhoneIcon", roles: withErpStaff(["hr", "dev", "owner"]) },
    {
        to: "/notifications",
        label: "Уведомления",
        icon: "InboxIcon",
        roles: withAllPanelRoles(["owner", "hr", "dev", "lead", "art"]),
        hidden: true,
    },
    // Hidden from sidebar; route/page kept for possible future use
    { to: "/logs", label: "Журнал", icon: "FileTextIcon", roles: withErpStaff(["owner", "hr", "dev"]), hidden: true },
    { to: "/candidate-portrait", label: "Портрет кандидатов", icon: "FileTextIcon", roles: withLeaders(withErpStaff(["owner", "lead", "dev"])) },
    { to: "/departments", label: "Профили отделов", icon: "BuildingIcon", roles: withLeaders(withErpStaff(["owner", "lead", "dev", "hr"])) },
    // «Компания» — структура + люди + KPI по подразделениям, правка №35, Чат 6 [Claude] 2026-11-03.
    { to: "/company", label: "Компания", icon: "BuildingIcon", roles: withLeaders(withErpStaff(["owner", "lead", "dev", "hr"])) },
    { to: "/organization", label: "Структура компании", icon: "BuildingIcon", roles: withLeaders(withErpStaff(["owner", "lead", "dev", "hr"])) },
    // KPI (КПЭ) — правка №10, Чат 6 [Claude] 2026-11-02. С №35 (03.11) кто что видит решают правила «KPI → Настройки → Доступ»
    // на backend; без доступа страница так и скажет. Руководителям (leader) пункт меню не показываем — свои таблицы КПЭ
    // они видят в «Компании» и открывают месяц по ссылке (скрытый пункт /kpi/months).
    { to: "/kpi", label: "KPI", icon: "BarChartIcon", roles: withErpStaff(["owner", "lead", "hr", "dev"]) },
    { to: "/kpi/months", label: "KPI — месяц", icon: "BarChartIcon", roles: withLeaders(withErpStaff(["owner", "lead", "hr", "dev"])), hidden: true },
    { to: "/blacklist", label: "Черный список", icon: "BanIcon", roles: withErpStaff(["owner", "hr", "dev"]) },
    { to: "/archive", label: "Архив кандидатов", icon: "ArchiveIcon", roles: withErpStaff(["hr", "owner", "dev"]) },
    // Personal page — any logged-in panel role (incl. ERP). Hidden from main menu; opened from sidebar avatar.
    {
        to: "/profile",
        label: "Профиль",
        icon: "UsersIcon",
        roles: withAllPanelRoles(["owner", "hr", "dev", "lead", "art"]),
        hidden: true,
    },
    // Personal menu settings (правка №5) — any signed-in panel role; opened from the sidebar footer.
    {
        to: "/settings/menu",
        label: "Настройка меню",
        icon: "SettingsIcon",
        roles: withAllPanelRoles(["owner", "hr", "dev", "lead", "art"]),
        hidden: true,
    },
];

/**
 * Default menu order — by the candidate path (согласовано 02.11.2026, правка №5):
 * общее → заявка → вакансия → кандидат → собеседование → тесты → документы →
 * сотрудник → обучение → аналитика и связь → справочники.
 * Items not listed here follow at the end.
 */
export const MENU_ORDER = [
    "/dashboard",
    "/ai-assistant",
    "/requests",
    "/vacancies",
    "/candidates",
    "/autopilot",
    "/calendar",
    "group:tests",
    "group:documents",
    "/employees",
    "/learning",
    "/analytics",
    "/calls",
    "/contacts",
    "/events",
    "/logs",
    "/candidate-portrait",
    "/departments",
    "/company",
    "/organization",
    "/kpi",
    "/blacklist",
    "/archive",
];

/**
 * Меню по умолчанию — дерево «А+Б» (правка №52, решение Светы 03.11.2026 12:29 и 17:10):
 * Главная · AI-консультант · Рекрутинг · Календарь · Люди · Компания · Аналитика · Архив и справочники.
 * Группы бывают вложенными (два уровня: «Тестирование» внутри «Рекрутинга», «Оформление» внутри «Людей»).
 * Листья — пути из NAV_ITEMS (права и подписи берутся оттуда). Пункт NAV_ITEMS, которого нет в дереве
 * и который не скрыт, у всех встаёт в конец меню — новые разделы добавляйте в NAV_ITEMS как раньше.
 * Права не меняются: дерево только раскладывает то, что роль и так может открыть.
 */
export const MENU_TREE = [
    "/dashboard",
    "/ai-assistant",
    {
        key: "group:recruiting",
        label: "Рекрутинг",
        icon: "BriefcaseIcon",
        children: [
            "/vacancies",
            "/requests",
            "/candidate-portrait",
            "/candidates",
            "/contacts",
            "/autopilot",
            "/calls",
            { key: "group:tests", label: "Тестирование", icon: "ClipboardIcon", children: ["/tests", "/tests/results", "/tests/unlinked"] },
        ],
    },
    { key: "group:calendar", label: "Календарь", icon: "CalendarIcon", children: ["/calendar", "/events"] },
    {
        key: "group:people",
        label: "Люди",
        icon: "UsersIcon",
        children: [
            { key: "group:documents", label: "Оформление", icon: "FileTextIcon", children: ["/candidate-documents", "/employee-contracts"] },
            "/tests/adaptation",
            "/employees",
            "/learning",
        ],
    },
    { key: "group:company", label: "Компания", icon: "BuildingIcon", children: ["/company", "/organization", "/departments", "/kpi"] },
    { key: "group:analytics", label: "Аналитика", icon: "BarChartIcon", children: ["/analytics"] },
    { key: "group:archive", label: "Архив и справочники", icon: "ArchiveIcon", children: ["/blacklist", "/archive"] },
];

/** Версия раскладки по умолчанию. Сменили дерево — увеличьте: личный порядок у всех сбросится (скрытые пункты и стартовая сохранятся). */
export const MENU_VERSION = 2;
/** «top» — группы слева, разделы группы сверху строкой (как в Битрикс24); «side» — всё слева раскрывающимся списком. */
export const MENU_LAYOUTS = ["top", "side"];
export const DEFAULT_MENU_LAYOUT = "top";

export const EMPTY_MENU_PREFS = Object.freeze({
    order: [],
    hidden: [],
    children: {},
    home: null,
    version: MENU_VERSION,
    layout: null,
    open: [],
});

export function isNavGroup(item) {
    return Boolean(item && Array.isArray(item.children));
}

/** Stable key of a menu item: its path, or `group:*` for a group. */
export function navItemKey(item) {
    if (!item) return "";
    return isNavGroup(item) ? (item.key || `group:${item.label}`) : item.to;
}

function sortByOrder(list, order, keyOf) {
    const idx = new Map();
    order.forEach((key, i) => {
        if (!idx.has(key)) idx.set(key, i);
    });
    return list
        .map((item, i) => ({ item, i, k: idx.has(keyOf(item)) ? idx.get(keyOf(item)) : -1 }))
        .sort((a, b) => {
            // Unknown items (new ones) go to the end, in their default order.
            if (a.k === -1 && b.k === -1) return a.i - b.i;
            if (a.k === -1) return 1;
            if (b.k === -1) return -1;
            return a.k - b.k;
        })
        .map((x) => x.item);
}

/** Menu allowed for the role, in the default order (no personal settings). */
/** Правка №58: при включённых правах «Доступов» — по правам, иначе по роли ERP, как раньше. */
function allowedFor(entry, role) {
    const byRights = entry.to ? pathAllowedByRights(entry.to) : null;
    return byRights === null ? entry.roles.includes(role) : byRights;
}

/**
 * Плоский список разделов (как до правки №52): одноуровневые группы «Тесты» и «Работа с документами».
 * Оставлен для тех, кому нужен просто перечень разделов роли (планшет руководителя №41 — ссылки,
 * проверки прав №58). Боковое меню строится из дерева — menuTreeForRole / menuForUser.
 */
export function menuItemsForRole(role) {
    const items = NAV_ITEMS.filter((item) => !item.hidden && (isNavGroup(item) ? (rightsOn() || item.roles.includes(role)) : allowedFor(item, role))).map((item) => {
        if (!isNavGroup(item)) return item;
        return {
            ...item,
            children: item.children.filter((c) => !c.hidden && allowedFor(c, role)),
        };
    }).filter((item) => !isNavGroup(item) || item.children.length > 0);

    return sortByOrder(items, MENU_ORDER, navItemKey);
}

/** Все листья NAV_ITEMS (в т. ч. из старых групп) по пути. */
function leafIndex() {
    const map = new Map();
    NAV_ITEMS.forEach((item) => {
        if (isNavGroup(item)) item.children.forEach((c) => map.set(c.to, c));
        else map.set(item.to, item);
    });
    return map;
}

/** Дерево меню, доступное роли, в порядке по умолчанию (без личных настроек). Пустые группы убираются. */
export function menuTreeForRole(role) {
    const leaves = leafIndex();
    const used = new Set();
    const build = (node) => {
        if (typeof node === "string") {
            const leaf = leaves.get(node);
            used.add(node);
            if (!leaf || leaf.hidden || !allowedFor(leaf, role)) return null;
            return leaf;
        }
        const children = node.children.map(build).filter(Boolean);
        return children.length ? { ...node, children } : null;
    };
    const tree = MENU_TREE.map(build).filter(Boolean);
    leaves.forEach((leaf, path) => {
        if (!used.has(path) && !leaf.hidden && allowedFor(leaf, role)) tree.push(leaf);
    });
    return tree;
}

const asKeyList = (value) => (Array.isArray(value) ? value.filter((v) => typeof v === "string" && v) : []);

/**
 * Defensive copy of stored settings (server or cache may hold anything).
 * Настройки старой раскладки (version ≠ MENU_VERSION): личный порядок и раскрытые группы сбрасываются
 * («встаёт всем», 03.11.2026), скрытые пункты, стартовая страница и режим подменю сохраняются.
 */
export function normalizeMenuPrefs(raw) {
    const src = raw && typeof raw === "object" ? raw : {};
    const current = src.version === MENU_VERSION;
    const children = {};
    if (current && src.children && typeof src.children === "object" && !Array.isArray(src.children)) {
        Object.entries(src.children).forEach(([group, list]) => {
            if (typeof group === "string" && group.startsWith("group:")) children[group] = asKeyList(list);
        });
    }
    const home = typeof src.home === "string" && src.home.startsWith("/") && !src.home.startsWith("//") ? src.home : null;
    return {
        order: current ? asKeyList(src.order) : [],
        hidden: asKeyList(src.hidden),
        children,
        home,
        version: MENU_VERSION,
        layout: MENU_LAYOUTS.includes(src.layout) ? src.layout : null,
        open: current ? asKeyList(src.open).filter((k) => k.startsWith("group:")) : [],
    };
}

/** Режим подменю пользователя: «top» (как в Битрикс24) или «side». */
export function menuLayoutOf(prefs) {
    const p = prefs && typeof prefs === "object" ? prefs : {};
    return MENU_LAYOUTS.includes(p.layout) ? p.layout : DEFAULT_MENU_LAYOUT;
}

function arrange(items, p, hidden, topLevel) {
    const order = topLevel ? p.order : null;
    return sortByOrder(items, order || [], navItemKey).map((item) => {
        const key = navItemKey(item);
        if (!isNavGroup(item)) return { ...item, key, visible: !hidden.has(key) };
        const children = sortByOrder(item.children, p.children[key] || [], navItemKey);
        return { ...item, key, visible: !hidden.has(key), children: arrange(children, { ...p, order: [] }, hidden, false) };
    });
}

/**
 * Дерево меню роли в личном порядке пользователя, с флагом `visible` у каждого пункта и группы —
 * для экрана «Настройка меню» (скрытые пункты в нём остаются).
 */
export function personalMenuLayout(role, prefs) {
    const p = normalizeMenuPrefs(prefs);
    return arrange(menuTreeForRole(role), p, new Set(p.hidden), true);
}

function onlyVisible(items) {
    return items
        .filter((item) => item.visible)
        .map((item) => (isNavGroup(item) ? { ...item, children: onlyVisible(item.children) } : item))
        .filter((item) => !isNavGroup(item) || item.children.length > 0);
}

/** Меню пользователя: личный порядок, скрытые пункты и пустые группы убраны. */
export function menuForUser(role, prefs) {
    return onlyVisible(personalMenuLayout(role, prefs));
}

/** Все пути-листья пункта (для группы — со всех уровней). */
export function leafPaths(item) {
    if (!item) return [];
    return isNavGroup(item) ? item.children.flatMap(leafPaths) : [item.to];
}

/** Первый раздел группы (куда ведёт клик по группе в режиме «сверху»). */
export function firstLeafPath(item) {
    return leafPaths(item)[0] || null;
}

/**
 * Где в меню находится открытая страница: [ключ верхнего пункта, …, путь листа].
 * Совпадение по самому длинному пути-префиксу (/employees/12 → «Сотрудники», /tests/results/5 → «Результаты»).
 * Нет совпадения — [].
 */
export function activeTrail(items, pathname) {
    const path = String(pathname || "");
    let best = [];
    let bestLen = -1;
    const walk = (list, trail) => {
        list.forEach((item) => {
            const key = navItemKey(item);
            if (isNavGroup(item)) {
                walk(item.children, [...trail, key]);
                return;
            }
            const to = item.to;
            const hit = path === to || (to !== "/" && path.startsWith(`${to}/`));
            if (hit && to.length > bestLen) {
                bestLen = to.length;
                best = [...trail, to];
            }
        });
    };
    walk(items || [], []);
    return best;
}

/** Pages a user may pick as the start page: every menu page their role can open. */
export function startPageOptions(role) {
    const out = [];
    const walk = (items, prefix) => {
        items.forEach((item) => {
            if (isNavGroup(item)) walk(item.children, [...prefix, item.label]);
            else out.push({ to: item.to, label: [...prefix, item.label].join(" → ") });
        });
    };
    walk(menuTreeForRole(role), []);
    return out;
}

export function rolesForPath(path) {
    if (path.startsWith("/vacancies/") && path.endsWith("/cold-search")) {
        return rolesForPath("/candidates");
    }
    if (path.startsWith("/vacancies/") && path.endsWith("/tests")) {
        return rolesForPath("/vacancies");
    }
    for (const item of NAV_ITEMS) {
        if (isNavGroup(item)) {
            const child = item.children.find((c) => c.to === path);
            if (child) return child.roles;
            continue;
        }
        if (item.to === path) return item.roles;
    }
    // Nested detail paths under /tests/results/:id inherit results roles
    if (path.startsWith("/tests/results")) {
        return rolesForPath("/tests/results");
    }
    if (path.startsWith("/tests/adaptation")) {
        return rolesForPath("/tests/adaptation");
    }
    if (path === "/tests/new" || /^\/tests\/\d+\/edit$/.test(path)) {
        return rolesForPath("/tests");
    }
    return [];
}

function firstPath(items) {
    for (const item of items) {
        const path = firstLeafPath(item);
        if (path) return path;
    }
    return null;
}

/**
 * Start page for this user: the personal start page if the role can still open
 * it, otherwise the first item of their (personal) menu, otherwise the first
 * page the role allows, otherwise the profile.
 */
export function defaultPathForRole(role, prefs) {
    const p = normalizeMenuPrefs(prefs);
    if (p.home && startPageOptions(role).some((o) => o.to === p.home)) return p.home;
    const fromMenu = firstPath(menuForUser(role, p)) || firstPath(menuTreeForRole(role));
    if (fromMenu) return fromMenu;
    const profileRoles = rolesForPath("/profile");
    if (!role || rightsOn() || profileRoles.includes(role)) return "/profile";
    return "/unauthorized";
}

export function psychPublicTakePath(instrumentId) {
    return `/take/${encodeURIComponent(instrumentId)}`;
}

export function professionalPublicTakePath(testId) {
    return `/take/test/${encodeURIComponent(testId)}`;
}
